import { useEffect, useState, type ReactNode } from 'react';
import { getSocket } from '@/lib/socket';
import { useAppUser } from '@/lib/useAppUser';
import { clientErrorReporter } from './client-error-reporter';
import { DEFAULT_PRIVATE_POLICIES } from './contracts';
import { adapt } from './plaee-event-adapter';
import { PrivateEventBus } from './private/private-event-bus';
import { PrivateEventBusProvider } from './private/private-event-bus-context';
import { usePrivateEventBus } from './private/use-private-event-bus';
import { useRealtimeManager, usePlaeeClient } from './realtime-manager-ctx';
import { installSocketIoPrivateIngest } from './socket-io-private-ingest';

let singletonBus: PrivateEventBus | null = null;

function getBus(): PrivateEventBus {
  if (singletonBus === null) {
    singletonBus = new PrivateEventBus(DEFAULT_PRIVATE_POLICIES);
    if (import.meta.env.DEV) {
      // Dev-only: expose the bus on window so the user can poke at policies
      // from devtools. Never reference from feature code.
      (globalThis as unknown as { __realtimeBus?: PrivateEventBus }).__realtimeBus = singletonBus;
    }
  }
  return singletonBus;
}

/**
 * Mounts the {@link PrivateEventBusProvider} and wires both transports into it:
 *  - Path A: existing `/realtime` Socket.IO connection (backend source)
 *  - Path B: direct Plaee Centrifugo connection (direct source), reusing the
 *    singleton client from {@link RealtimeManagerProvider}.
 *
 * Must be mounted inside a `<RealtimeManagerProvider>` so the shared
 * Centrifuge connection is available for the private channel subscription.
 */
export function RealtimePrivateBridge({ children }: { children?: ReactNode }): ReactNode {
  const [bus] = useState<PrivateEventBus>(() => getBus());
  return (
    <PrivateEventBusProvider bus={bus}>
      <PrivateBridgeConsumers />
      {children}
    </PrivateEventBusProvider>
  );
}

/** Side-effect-only component — keeps the hook calls together in one place. */
function PrivateBridgeConsumers(): null {
  useDirectPrivateChannel();
  const bus = usePrivateEventBus();
  useEffect(() => {
    const socket = getSocket();
    const uninstall = installSocketIoPrivateIngest(socket, bus);
    return () => {
      uninstall();
    };
  }, [bus]);
  return null;
}

/**
 * Resolves the Plaee customer id (via `client.ensureAuth()`), then subscribes
 * to the per-user private channel through the ref-counted realtime manager.
 * The shared {@link PlaeeClient} owns the Centrifuge connection — both public
 * channels and this private channel ride the same socket.
 */
function useDirectPrivateChannel(): void {
  const user = useAppUser();
  const bus = usePrivateEventBus();
  const client = usePlaeeClient();
  const manager = useRealtimeManager();

  useEffect(() => {
    if (user === null) return;
    let cancelled = false;
    let unsubscribe: (() => void) | null = null;

    const start = async (): Promise<void> => {
      let customerId: string | null = client.getPlaeeCustomerId();
      if (customerId === null) {
        try {
          const bundle = await client.ensureAuth();
          customerId = bundle.plaeeCustomerId;
        } catch (err) {
          if (cancelled) return;
          clientErrorReporter.report({
            kind: 'plaee-realtime-auth-failed',
            detail: { message: err instanceof Error ? err.message : String(err) },
          });
          return;
        }
      }
      if (cancelled) return;
      if (customerId === null) {
        clientErrorReporter.report({
          kind: 'plaee-realtime-auth-missing-customer-id',
          detail: { userId: String(user.userId) },
        });
        return;
      }

      const userId = String(user.userId);
      unsubscribe = manager.subscribeUser(customerId, (raw) => {
        const out = adapt(raw, {
          userId,
          occurredAt: new Date().toISOString(),
        });
        if (out === null) return;
        bus.ingest({
          name: out.name,
          payload: out.payload,
          source: 'direct',
          plaeeEventId: out.plaeeEventId,
        });
      });
    };

    void start();

    return () => {
      cancelled = true;
      if (unsubscribe !== null) {
        unsubscribe();
        unsubscribe = null;
      }
    };
  }, [user, bus, client, manager]);
}
